// @ts-nocheck
import type { ComponentType } from 'react';
// Import ComponentType
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';

import { Location, locationName, locationUserPermission, OnboardingStatus, type UserPermission } from '@cobliteam/common-types';

import { useOnboardingStore } from '@cobliteam/onboarding';

import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import type FeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/featureFlags';
import usePermissions from '@cobliteam/hooks/src/usePermissions';

import { AccessDeniedPanel } from '@cobliteam/base-components';

import routes, { type IRoute } from '../../routes';

interface PrivateRouteProps {
  route: IRoute;
}

const recursivePaths = (routesDef: IRoute[]) => {
  const paths: IRoute[] = [];
  routesDef.map((r: IRoute) => {
    paths.push(r);
    if (r.routes) paths.push(...recursivePaths(r.routes));
  });
  return paths;
};

const PrivateRoute = ({ route }: PrivateRouteProps) => {
  const { isAccessAllowed } = usePermissions();
  const location = useLocation();
  const featureToggleFlags = useFeatureFlags();

  const routerSelected: IRoute | undefined = recursivePaths(routes).find((r: IRoute) => {
    const pathRegex = new RegExp(`^${r.path.replace(/:[a-zA-Z]+/g, '[a-zA-Z0-9-]+')}$`);
    return pathRegex.test(location.pathname);
  });

  const requiredPermission = locationUserPermission[routerSelected?.path as Location] as UserPermission;

  const publicRoutes: Location[] = [Location.LOGIN, Location.TOKEN_VALIDATION, Location.PASSWORD_RECOVERY, Location.PASSWORD_RESET];
  const hasOnboarding = useOnboardingStore((state) => state.onboardingStatus);

  const history = useHistory();

  // should be history push because the route is in another context
  if (hasOnboarding === OnboardingStatus.INITIAL) {
    history.push(Location.ONBOARDING_SETUP);
    return null;
  }

  // feature flag
  if (
    route.featureFlag &&
    // @ts-ignore
    !featureToggleFlags[route.featureFlag as FeatureFlags]
  )
    return null;

  // redirect to home / if path is empty
  if (location.pathname === Location.EMPTY) return <Redirect to={Location.HOME} />;

  // access denied
  if (routerSelected && !isAccessAllowed(requiredPermission)) return <AccessDeniedPanel pageDescription={routerSelected?.accessDeniedDescription || ''} />;

  // after login and router is public - hotfix
  if (!routerSelected && publicRoutes.includes(location.pathname as Location)) return <Redirect to={Location.HOME} />;

  // router not found
  if (!routerSelected) return <Redirect to={Location.NOT_FOUND} />;

  // change title
  if (location.pathname === routerSelected?.path) {
    document.title = `Cobli - ${routerSelected.title || locationName[routerSelected.path as Location] || ''}`;
  }

  return (
    <Route
      path={route.path}
      render={(props) => {
        const Component = route.component as ComponentType<any>; // Cast route.component as ComponentType
        return <Component {...props} routes={route.routes} />;
      }}
      exact={route.exact}
    />
  );
};

export default PrivateRoute;
